import { Component, Injector, Input, OnInit } from '@angular/core';
import { take } from 'rxjs/operators';
import { WidgetVirtualBase } from '../../widgetVirtualBase';
import { HCashFlowWidgetPageData } from './h-cash-flow-widget-page-data';
import { ObjectUtil } from '../../../../../components/common/object-util/object-util';
import * as moment from 'moment';
import { environment } from '../../../../../../environments/environment';

/**
 * HCashFlow Widget
 */
@Component({
  selector: 'h-cash-flow-widget',
  templateUrl: './h-cash-flow-widget.component.html',
  styleUrls: ['../../common/css/general.scss', './h-cash-flow-widget.component.scss']//,
  //changeDetection: ChangeDetectionStrategy.OnPush
})
export class HCashFlowWidgetComponent extends WidgetVirtualBase implements OnInit {

  /**
   * Widget Guid
   */
  @Input() widgetGuid: string;

  /**
   * leftColumnWidth: calculated by calculateStyles function to fit data in widget depending on years
   */
  leftColumnWidth: number;

  /**
   * flexColumnWidth: calculated by calculateStyles function to fit data in widget depending on years
   */
  flexColumnWidth: number;

  /**
   * leftColumnWidthPx: calculated by calculateStyles function to fit data in widget depending on years
   */
  leftColumnWidthPx: string;

  /**
   * flexColumnWidthPx: calculated by calculateStyles function to fit data in widget depending on years
   */
  flexColumnWidthPx: string;

  /**
   * leftColumnWidthMonthWithPx: calculated by calculateStyles function to fit data in widget depending on years
   */
  leftColumnWidthMonthWithPx: string;

  /**
   * flexColumnWidthMonthWithPx: calculated by calculateStyles function to fit data in widget depending on years
   */
  flexColumnWidthMonthWithPx: string;

  /**
   * flexFontSizeUpper: calculated by calculateStyles function to fit data in widget depending on years
   */
  flexFontSizeUpper: number;

  /**
   * flexFontRatio: calculated by calculateStyles function to fit data in widget depending on years
   */
  flexFontRatio: number;

  /**
   * chartsRowspan: calculated by calculateStyles function to fit data in widget depending on years
   */
  chartsRowspan: any;

  /**
   * flexFontSizeLowerLeftTable: calculated by calculateStyles function to fit data in widget depending on years
   */
  flexFontSizeLowerLeftTable: number;

  /**
   * flexHeightLowerLeftTable: calculated by calculateStyles function to fit data in widget depending on years
   */
  flexHeightLowerLeftTable: number;

  /**
   * flexLineHeightLowerLeftTable: calculated by calculateStyles function to fit data in widget depending on years
   */
  flexLineHeightLowerLeftTable: number;

  /**
   * Are charts ready to be displayed?
   */
  chartsReady = false;

  /**
   * Chart LostIncome series
   */
  public seriesLostIncome: any[] = [];

  /**
   * Chart CashFlowBeforeDebtService series
   */
  public seriesCashFlowBeforeDebtService: any[] = [];

  /**
   * Chart RunningYield series
   */
  public seriesRunningYield: any[] = [];

  /**
   * Chart VacancyRate series
   */
  public seriesVacancyRate: any[] = [];

  /**
   * Chart categories
   */
  public categories: number[] = [];

  /**
   * Original widget data
   * Used to distribute data among widget pages
   */
  private originalData: any = null;

  /**
   * Chart LostIncome Title
   * text will be translated in prepareCharts
   */
  public chartLostIncomeTitle = {
    text: 'hCashFlow.labelLostIncome', font: '12px Arial', padding: 5
  };

  /**
   * Chart VacancyRate Title
   * text will be translated in prepareCharts
   */
  public chartVacancyRateTitle = {
    text: 'hCashFlow.labelVacancyRate', font: '12px Arial', rotation: '90', padding: 5
  };

  /**
   * Chart DcfCashFlowBeforeDebtService Title
   * text will be translated in prepareCharts
   */
  public chartDcfCashFlowBeforeDebtServiceTitle = {
    text: 'hCashFlow.dcfCashFlowBeforeDebtService', font: '12px Arial', padding: 5
  };

  /**
   * Chart DcfRunningYield Title
   * text will be translated in prepareCharts
   */
  public chartDcfRunningYieldTitle = {
    text: 'hCashFlow.dcfRunningYield', font: '12px Arial', rotation: '90', padding: 5
  }

  /**
   * Hide the gross capital value (rounded)?
   */
  public get hideGrossCapitalValueRounded(): boolean {
    return !!this.widgetConfig.widgetSettings.customSettings?.hideGrossCapitalValueRounded;
  }

  /**
   * Hide the purchasers costs?
   */
  public get hidePurchasersCosts(): boolean {
    return !!this.widgetConfig.widgetSettings.customSettings?.hidePurchasersCosts;
  }

  /**
   * starting background color CSS class
   */
  private get startBackgroundColorClass(): string {
    // It is not defined with which color we start so assume light-grey-line
    return 'light-grey-line';
  }

  /**
   * alternate background color CSS class
   */
  private get alternateBackgroundColorClass(): string {
    // It is not defined with which color is alternate so assume white-line
    return 'white-line';
  }

  /**
   * Background CSS class for grossCapitalValueRounded row
   */
  public get grossCapitalValueRoundedBackgroundClass(): string {
    return !this.hideGrossCapitalValueRounded ? this.startBackgroundColorClass : this.alternateBackgroundColorClass;
  }

  /**
   * Background CSS class for purchasersCosts row
   */
  public get purchasersCostsBackgroundClass(): string {
    return this.hidePurchasersCosts
      ? this.grossCapitalValueRoundedBackgroundClass //keep the same (row not displayed) so next can alternate
      : this.grossCapitalValueRoundedBackgroundClass === this.startBackgroundColorClass ? this.alternateBackgroundColorClass : this.startBackgroundColorClass;
  }

  /**
   * Background CSS class for dcfMarketValue row
   */
  public get dcfMarketValueBackgroundClass(): string {
    //TO CHECK: maybe return white if both grossCapitalValueRounded & purchasersCosts are hidden?
    return this.purchasersCostsBackgroundClass === this.startBackgroundColorClass ? this.alternateBackgroundColorClass : this.startBackgroundColorClass;
  }

  /**
   * Constructor
   * @param injector
   */
  constructor(injector: Injector) {
    super(injector, 'hCashFlow');
  }

  /**
   * Initializes the component
   */
  ngOnInit() {
    this.executeWidgetDataLoadedInWidgetClass = true;
    // will call registerEvents and onInitialized when config (this.widgetConfig and this.page) is ready
    this.initWidgetConfig();
  }

  /**
   * Register Master widget events
   */
  registerEventsMaster(): void {
    this.registerMasterOrSlaveEvents();
  }

  /**
   * Register Slave widgets events
   */
  registerEventsSlave(): void {
    this.registerMasterOrSlaveEvents();
  }

  /**
   * Register Master or Slave widgets events (same code)
   */
  registerMasterOrSlaveEvents() : void{
    this.useReadOnlySaveChangesHandler();
    this.onEvents.push(this.eventBus.subscribeEvent('hCashFlow:syncCustomSettings', (customSettings) => {
      this.widgetConfig.widgetSettings.customSettings = ObjectUtil.deepCopy(customSettings);
    }));
  }

  /**
   * Setup widget's layout if required
   */
  setupLayouts() {
    let fixLayouts = this.availableLayouts.length !== 2;
    fixLayouts = fixLayouts || this.availableLayouts[0] !== 'Summary';
    fixLayouts = fixLayouts || this.availableLayouts[1] !== 'Detailed';
    fixLayouts = fixLayouts || this.availableLayouts.indexOf(this.currentLayout) === -1;

    if (fixLayouts) {
      this.availableLayouts = ['Summary', 'Detailed'];
      this.currentLayout = 'Summary';

      const setting = this.widgetConfig.widgetSettings;
      setting.availableLayouts = this.availableLayouts;
      setting.currentLayout = this.currentLayout;
    }
  }

  /**
   * Initialize the settings
   */
  private initSettings(): void {
    //init if custom setting not yet exists
    if (!this.widgetConfig.widgetSettings.customSettings) {
      this.widgetConfig.widgetSettings.customSettings = {
        hideGrossCapitalValueRounded: false,
        hidePurchasersCosts: false
      }
    }
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   * This function is called for Master widget
   */
  onInitializedMaster(): void {
    this.setupLayouts();
    this.initSettings();

    this.mockupData$ = this.widgetService.loadMockup('hcashflow').pipe(take(1));

    this.widgetData$ = this.mockup ?
      this.mockupData$
      : this.widgetService.load('hcashflow', this.getReport()).pipe();
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   * This function is called for Slave widgets
   */
  onInitializedSlave(): void {
    this.setupLayouts();

    this.widgetData = new HCashFlowWidgetPageData(this);
    this.widgetData.pageNo = this.widgetConfig.widgetSettings.slavePageNo;

    const intervalId = setInterval(() => {
      if (!this.widgetData.slaveDataUpdated && !this.destroyed) {
        const pageIndex: number = this.widgetData.pageNo;
        const slavePageData = this.virtualPagesService.getPageData(this.WIDGET_MASTER_SLAVE_MSG, pageIndex);
        //if data exists set it to virtual page
        if (slavePageData) {
          !environment.production && console.log(`Returning slave page ${pageIndex} from page cache`);
          slavePageData.slaveDataUpdated = true;
          this.eventBus.broadcastEvent(this.WIDGET_MASTER_SLAVE_MSG, { cmd: "setSlaveData", value: slavePageData });
        }
      } else {
        clearInterval(intervalId);
      }
    }, 20);
  }

  /**
   * slaveWidgetMessageHandled event handler
  * @param cmd {string} Command handled
   */
  protected slaveWidgetMessageHandled(cmd: string) {
    if (cmd === 'setSlaveData') {
      this.calculateStyles(this.widgetData.years.length);
      this.prepareCharts(this.widgetData);
    }
  }

  /**
   * Layout changed event handler
  * @param newLayout {string} New layout
   */
  public layoutChanged(_newLayout: string): void {
    if (!this.isMasterWidget) {
      return;
    }

    const layout = this.currentLayout;
    this.widgetConfig.widgetSettings.currentLayout = layout;

    const data = ObjectUtil.deepCopy(this.originalData);

    this.widgetData = null;

    setTimeout(() => {
      const hasSlavePages = this.widgetPages.length > 1;
      if (hasSlavePages) {
        this.broadcastFromMaster({ cmd: 'deleteSlavePages' });
        // give some time for pages to be deleted
        setTimeout(() => {
          this.calculateData(data);
        }, 200);
      } else {
        this.calculateData(data);
      }
    });
  }

  /**
   * TrackBy function used by Angular *ngFor trackBy
   * @param _index {number} Index
   * @param el {any} Element
   */
  public trackByYear(_index: number, el: any): number {
    return el.dcfYear;
  }


  /**
   * TrackBy function used by Angular *ngFor trackBy
   * @param _index {number} Index
   * @param el {any} Element
   */
  public rackByRow(_index: number, el: any): number {
    return el.field + el.label;
  }

  /**
   * TrackBy function used by Angular *ngFor trackBy
   * @param index {number} Index
   * @param _el {any} Element
   */
  public trackByRowEntry(index: number, _el: any): number {
    return index;
  }

  /**
   * TrackBy function used by Angular *ngFor trackBy
   * @param _index {number} Index
   * @param el {any} Element
   */
  public trackByChartSeries(_index: number, el: any): number {
    return el.name;
  }

  /**
   * Calculate UI style parameters based on number of years displayed
  * @param years {any} Number of years widget displays
   */
  private calculateStyles(years) {
    if (years <= 21) {
      this.leftColumnWidth = 30;
      this.flexColumnWidth = 70 / years;//USED
    }
    else {
      this.leftColumnWidth = 20;
      this.flexColumnWidth = 80 / years;//USED
    }

    this.leftColumnWidthPx = this.leftColumnWidth + "%";
    this.flexColumnWidthPx = this.flexColumnWidth + "%";

    this.leftColumnWidthMonthWithPx = 30 + "%";
    this.flexColumnWidthMonthWithPx = (70 / 12) + "%";

    //FONTS
    const originalFontSize = 12;//for 11 years(Bug 5934: Live Bug: Com: HCashFlow Widget - Layout Year 1-30 --> Font size to scale down according to the number of years shown)

    this.flexFontSizeUpper = originalFontSize;
    if (years > 12) {
      this.flexFontSizeUpper = 12;
    }

    if (years > 24) {
      this.flexFontSizeUpper = 11;
    }

    if (years > 26) {
      this.flexFontSizeUpper = 9;
    }

    this.flexFontRatio = this.flexFontSizeUpper / originalFontSize;

    const magic = 0;
    const lowerTableYearsHeight = 200 - magic;

    let lowerTableRowSize = (lowerTableYearsHeight / years) - 1;
    lowerTableRowSize = Math.min(lowerTableRowSize, 13);

    this.flexFontSizeLowerLeftTable = lowerTableRowSize;
    this.flexHeightLowerLeftTable = lowerTableRowSize;
    this.flexLineHeightLowerLeftTable = lowerTableRowSize;

    this.chartsRowspan = 6 + years;

    if (years <= 21) {
      this.flexFontSizeLowerLeftTable = this.flexFontSizeUpper;
    }
  }

  /**
   * Called by base class to let widget preprocess loaded data if required
   */
  protected preprocessLoadedData(data) {
    for (const year of data.years) {
      if (year.dcfSubItemList) {
        try {
          year.dcfSubItemList = JSON.parse(year.dcfSubItemList).SubItemListEntry;
        } catch (e) {
          //Ignore
        }
      }
    }

    this.originalData = ObjectUtil.deepCopy(data);
    return this.calculateData(data);
  }

  /**
   * Calculates/prepares data to be displayed in widget
  * @param data {any} Data
   */
  protected calculateData(data) {
    this.calculateStyles(data.years.length);

    data.rows = [];
    data.rows.push(...this.buildRow(data, 'labelPotentialGrossRevenue', true, true, false));/* this is only label, not data */
    data.rows.push(...this.buildRow(data, 'dcfBaseRentalValue'));
    data.rows.push(...this.buildRow(data, 'dcfAbsorptionTurnoverVacancy'));
    data.rows.push(...this.buildRow(data, 'dcfFreeRent'));
    data.rows.push(...this.buildRow(data, 'dcfTotalExpenseRecoveries'));
    data.rows.push(...this.buildRow(data, 'dcfOtherRevenues'));
    data.rows.push(...this.buildRow(data, 'dcfBaseRentAbatements'));
    data.rows.push(...this.buildRow(data, 'dcfTotalMiscellaneousRent'));
    data.rows.push(...this.buildRow(data, 'dcfCPIOtherAdjustmentRevenue'));
    data.rows.push(...this.buildRow(data, 'dcfTurnOverRent'));
    data.rows.push(...this.buildRow(data, 'dcfTotalPotentialGrossRevenue', false, true, true));

    data.rows.push(...this.buildRow(data, 'BLANK SPACE', false, false, false, { isBlankSpace: true }));

    const rowDcfVacancyAllowance = this.buildRow(data, 'dcfVacancyAllowance');
    const rowDcfCreditLoss = this.buildRow(data, 'dcfCreditLoss');
    const rowDcfTotalVacancyAndCreditLoss = this.buildRow(data, 'dcfTotalVacancyAndCreditLoss');

    if (rowDcfVacancyAllowance.length || rowDcfCreditLoss.length || rowDcfTotalVacancyAndCreditLoss.length) {
      data.rows.push(...this.buildRow(data, 'labelVacancyAndCreditLoss', true, true, false));/* this is only label, not data */
      data.rows.push(...rowDcfVacancyAllowance);
      data.rows.push(...rowDcfCreditLoss);
      data.rows.push(...rowDcfTotalVacancyAndCreditLoss);
      data.rows.push(...this.buildRow(data, 'BLANK SPACE', false, false, false, { isBlankSpace: true }));
    }

    data.rows.push(...this.buildRow(data, 'dcfEffectiveGrossRevenue', false, true, true));
    data.rows.push(...this.buildRow(data, 'BLANK SPACE', false, false, false, { isBlankSpace: true }));

    data.rows.push(...this.buildRow(data, 'labelOperatingExpenses', true, true, false));/* this is only label, not data */
    data.rows.push(...this.buildRow(data, 'dcfManagementCosts'));
    data.rows.push(...this.buildRow(data, 'dcfMaintenanceCosts'));
    data.rows.push(...this.buildRow(data, 'dcfOtherCosts'));
    data.rows.push(...this.buildRow(data, 'dcfInsuranceCosts'));
    data.rows.push(...this.buildRow(data, 'dcfGroundTax'));
    data.rows.push(...this.buildRow(data, 'dcfVacancyCosts'));
    data.rows.push(...this.buildRow(data, 'dcfLeaseHoldCosts'));
    data.rows.push(...this.buildRow(data, 'dcfStructuralVacancyCosts'));
    data.rows.push(...this.buildRow(data, 'dcfFurtherSpecificCosts'));
    data.rows.push(...this.buildRow(data, 'dcfStatutoryExpenses'));
    data.rows.push(...this.buildRow(data, 'dcfNonRecoverableExpenses'));
    data.rows.push(...this.buildRow(data, 'dcfTotalOperatingExpenses', false, true, true));

    data.rows.push(...this.buildRow(data, 'BLANK SPACE', false, false, false, { isBlankSpace: true }));
    data.rows.push(...this.buildRow(data, 'dcfNetOperatingIncome', false, true, true));

    data.rows.push(...this.buildRow(data, 'dcfLeasingCapitalCosts'));
    data.rows.push(...this.buildRow(data, 'dcfLeasingAndCapitalCosts'));
    data.rows.push(...this.buildRow(data, 'dcfTenantImprovements'));
    data.rows.push(...this.buildRow(data, 'dcfLeasingCommissions'));
    data.rows.push(...this.buildRow(data, 'dcfCapitalExpenditures'));
    data.rows.push(...this.buildRow(data, 'dcfBuildingCostsSusidiesAndOtherCapitalExpenditures'));
    data.rows.push(...this.buildRow(data, 'dcfTotalLeasingAndCapitalCosts', false, true, true));

    data.rows.push(...this.buildRow(data, 'BLANK SPACE', false, false, false, { isBlankSpace: true }));
    data.rows.push(...this.buildRow(data, 'dcfCashFlowBeforeDebtService', false, true, true));

    data.rows.push(...this.buildRow(data, 'dcfTerminalValue'));

    data.rows.push(...this.buildRow(data, 'dcfNetCashFlow', false, true, true));

    data.rows.push(...this.buildRow(data, 'dcfRunningYield', false, false, false, { isBlankSpace: false, isPercentage: true, multiplyFactor: 100 }));

    //Task 14566: Widget - HCashFlow UI - Layout Versions
    // if not layout Detailed don't show subitems
    if (this.currentLayout !== 'Detailed') {
      data.rows = data.rows.filter((row) => !row.isSubItem);
    }

    this.prepareCharts(data);

    setTimeout(() => {
      this.setupWidgetViewModel(data);
      this.widgetData = this.widgetPages[0];//set own (master) page view data
    });

    return data;
  }

  /**
   * Setup widget ViewModel
   */
  setupWidgetViewModel(data) {

    if (!this.isMasterWidget) {
      return;
    }

    const currentPageData = new HCashFlowWidgetPageData(this, data);
    this.widgetPages.length = 0;
    this.widgetPages.push(currentPageData);

    currentPageData.processData();

    //save pages for slave pages (print!)
    this.virtualPagesService.setPagesData(this.WIDGET_MASTER_SLAVE_MSG, this.widgetPages);

    //create pages if required
    this.processWidgetSlavePages(this);
    //IMPORTANT: call widgetDataLoaded after slave pages are added to document, so documentDataLoaded is not fired too soon!!!
    this.page.widgetDataLoaded(this.widgetConfig.widgetSettings.id);
  }

  /**
   * Build new data row
  * @param data {any} Data array
  * @param field {any} Field name
  * @param isHeader {boolean} Is row header
  * @param isTotal {boolean} Is row total
  * @param isTotalGold {boolean} Is row total(gold)
  * @param paramsEx {any} Additional parameters
   */
  private buildRow(data, field, isHeader = false, isTotal = false, isTotalGold = false, paramsEx: any = null): any[] {
    const params = paramsEx || { isBlankSpace: false, isPercentage: false, multiplyFactor: 1 };
    // force boolean by using !!
    const isBlankSpace = !!params.isBlankSpace;
    const isPercentage = !!params.isPercentage;
    const ret = [];
    const mainRow = {
      isHeader: isHeader,
      isTotal: isTotal,
      isTotalGold: isTotalGold,
      field: field,
      label: field,
      entries: [],
      isEmpty: !isHeader && !isTotal && !isTotalGold && !isBlankSpace && this.isEmptyRow(data.years, field),
      isBlankSpace: isBlankSpace,
      isPercentage: isPercentage
    };

    const rowsToAdd = [];

    try {
      if (field === 'dcfCapitalExpenditures') {
        data.years[0][field + 'Index'] = '02';
      } else if (field === 'dcfOtherRevenues') {
        data.years[0][field + 'Index'] = '03';
      }

      if (data.years[0][field + 'Index'] && data.years[0].dcfSubItemList) {
        this.buildSubItemListRow(data, field, params, rowsToAdd);
      }

      if (!mainRow.isEmpty) {
        for (const entry of data.years) {
          mainRow.entries.push(entry[field] * params.multiplyFactor);
        }

        ret.push(mainRow);
      }

      ret.push(...rowsToAdd);

    } catch (e) {
      //Ignore
    }
    // }

    return ret;
  }

  /**
   * Build a sub item list row (subitems are just imported in batch)
   * @param data
   * @param field
   * @param params
   * @param rowsToAdd
   */
  private buildSubItemListRow(data: any, field: any, params: any, rowsToAdd: any[]) {
    const index = data.years[0][field + 'Index'];

    let matchingSubItemsYear0;
    if (index.startsWith("01-")) {
      matchingSubItemsYear0 = data.years[0].dcfSubItemList.filter((entry) => { return entry.Index !== index && entry.Index.indexOf(index) === 0; });
    }
    else {
      matchingSubItemsYear0 = data.years[0].dcfSubItemList.filter((entry) => { return entry.Index.indexOf(index) === 0; });
    }

    for (const subItem of matchingSubItemsYear0) {
      let isSubItem = false;
      let isVirtualMainRow = false;

      if (index.startsWith("01-")) {
        isSubItem = true;
      }
      else {
        const numDashes = subItem.Index.split('-').length - 1;
        isSubItem = numDashes === 2;
        isVirtualMainRow = numDashes === 1;
      }

      const subRow = {
        isHeader: false,
        isTotal: false,
        isTotalGold: false,
        field: index,
        label: subItem.Label.trim(),
        entries: [],
        isEmpty: false,
        isBlankSpace: false,
        isPercentage: false,
        isSubItem: isSubItem,
        isVirtualMainRow: isVirtualMainRow
      };

      for (const entry of data.years) {
        const subItemForYear = entry.dcfSubItemList.filter((entry) => { return entry.Index === subItem.Index; });
        if (subItemForYear.length) {
          subRow.entries.push(subItemForYear[0].Value * params.multiplyFactor * entry.currencyFactor);
        }
      }
      rowsToAdd.push(subRow);
    }
  }

  /**
   * Calculates if row is empty
  * @param data {any} Data array
  * @param field {any} Field name
   */
  private isEmptyRow(data, field) {
    for (const entry of data) {
      if (entry[field]) {
        return false;
      }
    }

    return true;
  }

  /**
   * Returns start date
  * @param date {Date} Date
  * @param index {number} Index
   */
  public getStartDate(date: Date, index: number): Date {
    const newDate = moment(date);
    newDate.add(11, 'M');

    const ret = newDate.toDate();
    ret.setFullYear(ret.getFullYear() + index - 1);
    return ret;
  }

  /**
   * Prepares/calculate charts data and appearance
  * @param data {any} Widget data
   */
  private prepareCharts(data: any) {

    //left chart series
    this.seriesLostIncome = [
      {
        name: 'first',
        color: '#E31613',
        data: data.years.map((entry) => entry.dcfAbsorptionTurnoverVacancy)
      }
    ];

    this.seriesVacancyRate = [
      {
        name: 'second',
        color: '#000000',
        data: data.years.map((entry) => entry.dcfVacancyRate * 100),
        title: this.chartVacancyRateTitle
      }
    ];

    //right chart series
    this.seriesCashFlowBeforeDebtService = [
      {
        name: 'first',
        color: '#E31613',
        data: data.years.map((entry) => entry.dcfCashFlowBeforeDebtService)
      }
    ];

    this.seriesRunningYield = [
      {
        name: 'second',
        color: '#000000',
        data: data.years.map((entry) => entry.dcfRunningYield * 100)
      }
    ];

    this.categories = data.years.map((_entry, index) => {
      return this.getStartDate(data.years[0].dcfStartDate, index + 1).getFullYear();
    });

    //translate chart titles
    this.chartLostIncomeTitle.text = this.reportDesignerTranslator.getString(this.chartLostIncomeTitle.text);
    this.chartVacancyRateTitle.text = this.reportDesignerTranslator.getString(this.chartVacancyRateTitle.text);
    this.chartDcfCashFlowBeforeDebtServiceTitle.text = this.reportDesignerTranslator.getString(this.chartDcfCashFlowBeforeDebtServiceTitle.text);
    this.chartDcfRunningYieldTitle.text = this.reportDesignerTranslator.getString(this.chartDcfRunningYieldTitle.text);

    this.chartsReady = true;
  }

  /**
   * Toggle the hide gross capital value (rounded) setting
   */
  public toggleHideGrossCapitalValueRounded(): void {
    const currentValue = this.widgetConfig.widgetSettings.customSettings.hideGrossCapitalValueRounded;
    this.widgetConfig.widgetSettings.customSettings.hideGrossCapitalValueRounded = !currentValue;
    this.syncCustomSettings();
  }

  /**
   * Toggle the hide purchasers costs setting
   */
  public toggleHidePurchasersCosts(): void {
    const currentValue = this.widgetConfig.widgetSettings.customSettings.hidePurchasersCosts;
    this.widgetConfig.widgetSettings.customSettings.hidePurchasersCosts = !currentValue;
    this.syncCustomSettings();
  }

  /**
   * Synchronize the custom settings
   */
  private syncCustomSettings(): void {
    this.eventBus.broadcastEvent('hCashFlow:syncCustomSettings', this.widgetConfig.widgetSettings.customSettings)
  }
}
